import { Subscription } from 'rxjs';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from '../shared/global.service';
import moment from 'moment';
import { SettingsService } from '../shared/api/resources/settings/settings.service';
import { Customer } from '../shared/api/model/customer';
import { WidgetService } from '../shared/api/widget/widget.service';
import { StaticUtils } from '../shared/utils/static-utils';
import { Promocode } from '../shared/api/model/promocode';
import { ToasterService } from '../shared/atoms/toaster/toaster.service';
import { CartService } from '../shared/cart/cart.service';
import { Booking } from '../shared/api/model/booking';
import { BookingShoppingCart } from '../shared/api/model/BookingShoppingCart';
import { BillingDetails } from '../shared/api/model/billing-details';
import { environment } from '../../environments/environment';
import { debounce } from 'lodash';
import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss']
})
export class SummaryComponent implements OnInit {
  private _promocode = '';

  utils = StaticUtils;
  moment = moment;
  sumOfGenderPriceQuantityToBook: number | null = null;
  missingSeatsToReachedMinimumLotation: number;
  nationalities = [];
  customer: Customer;
  termsAccepted = false;
  confirmEmail: string;
  emailValid = true;
  emailsMatch = true;
  isToHidePromocode = true as boolean;
  isPromocodesCollapsed = true;
  collapseStyles = {'overflow': 'hidden'};
  findingPromocode = false;
  promocodeNotValid = false;
  promocodeError: string;
  cart: BookingShoppingCart;
  billingDetails = new BillingDetails();
  isBillingCollapsed = true;
  billingCollapseStyles = {'overflow': 'hidden'};
  currentPromocode: string;
  shoppingCartChangeSub: Subscription;
  requiredDueBillingPrice = false;
  countryCode2: string | null = null;
  onDialInput: () => void;

  constructor(
    private _router: Router,
    private _globalService: GlobalService,
    private _settings: SettingsService,
    private _widgetService: WidgetService,
    private _cartService: CartService,
    private _toasterService: ToasterService) { }

  public async ngOnInit(): Promise<void> {
    if (this._globalService.skip_my_cart === true) {
      setTimeout(() => this._updateCart(true));
      this.shoppingCartChangeSub = this._cartService.shoppingCartChange.subscribe(() => this._updateCart());
    }

    this.termsAccepted = this._globalService.acceptedTerms;
    this.customer = this._globalService.customer || new Customer();
    this.confirmEmail = this._globalService.confirmEmail || '';
    this.updateData();
    this.cart = await this._cartService.getCart();
    this.currentPromocode = this.cart.attachPromocode;
    
    if (this.currentPromocode) {
      this.togglePromocodesCollapse();
    }

    this.isToHidePromocode = environment.isToHidePromocode;

    const hasInvoiceSystem = this.cart.bookings[0].creatorSupplier.settingsPresets.hasInvoiceSystem;

    if (hasInvoiceSystem && this.cart?.billingTotal >= 1000) {
      this.toggleBillingCollapse();
      this.requiredDueBillingPrice = true;
    }

    this.onDialInput = debounce(() => {
      this.updateCountryByDial();
    }, 1000);

    if (this.customer && this.customer.country) {
      const flatItems: any[] = this.nationalities.reduce((acc, cur) => {
        acc.push(...cur.items.map(item => item.value));
        
        return acc;
      }, []);
      
      const country = flatItems.find((n: any) => n.id === this.customer.country);
      
      if (country) {
        this.countryCode2 = country.code2;
      }
    }
  }

  updateCountryByDial() {
    let raw = (this.customer.dial || '').replace(/\D/g, '');

    // If it starts with “00”, remove it
    if (raw.startsWith('00')) {
      raw = raw.slice(2);
    }

    const country = this.findCountryByDial(raw);

    if (country) {
      this.applyCountryInfo(country);
    } else {
      this.resetCountryInfo();
    }
  }

  onCountrySelect(country: any, type: string) {
    this.applyCountryInfo(country, type);
  }

  private findCountryByDial(dialInput: string): any | null {
    let dial = (dialInput || '').replace(/\D/g, '');
    if (dial.startsWith('00')) dial = dial.slice(2);
    if (!dial) return null;

    const flatItems: any[] = this.nationalities.reduce((acc, cur) => {
      acc.push(...cur.items.map(item => item.value));
      
      return acc;
    }, []);

    // Filter country
    const country: any = flatItems.find((n: any) => n.dial === dial);

    if (country.length !== 0) {
        return country;
    } else {
        return null;
    }
  }

  private applyCountryInfo(country: any, type: string = 'customer') {
    if (type === 'billing') {
      this.billingDetails.country = country.id;
    } else {
      this.customer.country = country.id;
      this.customer.dial = country.code;
      this.customer.countryName = country.name;
      this.countryCode2 = country.code2;
    }
  }

  private resetCountryInfo() {
    this.customer.country = null;
    this.customer.dial = null;
    this.customer.countryName = '';
    this.countryCode2 = null;
  }

  countryString(val: string) {
    const flatItems: any[] = this.nationalities.reduce((acc, cur) => {
      acc.push(...cur.items.map(item => item.value));
      
      return acc;
    }, []);

    // Filter country
    const country: any = flatItems.find((n: any) => n.id === val);
    
    return country.name;
  }

  updateCountry(e: any) {
    this.customer.country = e.value.id;
    this.customer.phone =`${e.value.code}`;
    this.customer.dial = e.value.dial;
    this.customer.code = e.value.code;
  }

  private async _updateCart(forceUpdate = false): Promise<void> {
    this._globalService.loading = true;
    this.cart = await this._cartService.getCart(forceUpdate);
    this._globalService.loading = false;
  }

  public updateData(): void {
    this.nationalities = [];
    const topCodes = ['PT', 'GB', 'US', 'ES', 'FR'];
    const topCountries = this._settings.countries.filter(c => topCodes.includes(c.code2)).map(country => {
      return {
        label: country.name,
        value: country
      }
    });
    const allCountries = this._settings.countries.map(country => {
      return {
        label: country.name,
        value: country
      }
    });

    this.nationalities = [
        {
            groupLabel: 'Top 5 - Countries',
            items: topCountries
        },
        {
            groupLabel: 'All Countries',
            items: allCountries
        }
    ];
  }

  public async book(): Promise<void> {
    this._globalService.loading = true;
    await this.saveCustomer(true);

    if (this.cart.billingTotal === 0) {
      this._globalService.processingPayment = true;
      try {
        await this._widgetService.shoppingCartStripePaymentIntent(this.cart.id, this.cart.guid).toPromise();

        this.cart = await this._cartService.getCart();
        this._createAnalyticsEvent(this.cart);

        await this._router.navigate(['../thankyou/'], { 
          queryParamsHandling: 'merge' 
        });
      } catch (errorResult) {
        this._toasterService.showError(errorResult.error ? errorResult.error.message : errorResult.message);
      } finally {
        this._globalService.loading = false;
        this._globalService.processingPayment = false;
      }
    } else {
      await this._router.navigate(['../payment/'], { 
        queryParamsHandling: 'merge' 
      });
      this._globalService.loading = false;
    }
  }

  private _createAnalyticsEvent(cart: object): void {
    const products = [];

    cart['bookings'].forEach(booking => {
      products.push({
        'name': booking.event.product.name,
        'id': booking.event.product.id.toString(),
        'quantity': booking.paxTotal,
        'price': booking.billingTotal,
        'coupon': booking.attachPromocode,
      });
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'purchase',
      'ecommerce': {
        'currencyCode': 'EUR',
        'purchase': {
          'actionField': {
            'id': cart['id'],
            'affiliation': 'Pluralo Widget',
            'revenue': cart['billingTotal'],
            'coupon': cart['attachPromocode'],
          },
          products
        }
      }
    });
  }
  
  public showMessagePendingConfirm = (booking: Booking): boolean => {
    let event = booking.event
    let bookingGenderPrices = booking.bookingGenderPrices
    this.sumOfGenderPriceQuantityToBook = bookingGenderPrices
      .filter((gp: any) => gp.audienceType !== null) 
      .map((gp: any) => gp.quantityToBook)
      .reduce((prev: number, next: number) => prev + next, 0);
      
      const staticEvent = this._globalService.getStaticEvent(); // Static because event is setted on global service to access and keep the value static of event.quantityBooked 
      let totalPaxBooked = this.sumOfGenderPriceQuantityToBook + staticEvent.quantityBooked // Total pax of the booking and the pax in quantityBooked
      this.missingSeatsToReachedMinimumLotation = event.minimumLotation - staticEvent.quantityBooked

      if(totalPaxBooked < event.minimumLotation){
      return true
    } else return false
  }

  isPhoneValid(): boolean {
    // If there is no country defined, it is invalid
    if (!this.countryCode2) {
      return false;
    }

    // Normalize the country to uppercase and make the parse
    const iso2 = this.countryCode2.toUpperCase() as CountryCode;
    const phone = (this.customer.phone || '').trim();
    const phoneNumber = parsePhoneNumberFromString(phone, iso2);

    return phoneNumber ? phoneNumber.isValid() : false;
  }

  isPhoneEmpty() {
    return !this.customer.phone || this.customer.phone === '';
  }

  isDialEmpty() {
    return !this.customer.dial || this.customer.dial === '';
  }

  validateBillingDetails(): boolean {
    if (this.requiredDueBillingPrice) {
      const { vat, country, businessName, address } = this.billingDetails;
      return !(vat && country && businessName && address);
    }
  
    return false;
  }

  validateCustomerDetails(): boolean {
    const { name, phone, country, email } = this.customer || {};
  
    const isMissingRequiredFields = !name || !phone || !country || !email;
    const isEmailMismatch = this.confirmEmail?.toLowerCase() !== email?.toLowerCase();
    const isEmailInvalid = !this.validEmail(email);
    const isPhoneInvalid = !this.isPhoneValid();
  
    return isMissingRequiredFields || isEmailMismatch || isEmailInvalid || isPhoneInvalid;
  }

  validateCustomerEmail() {
    this.emailValid = this.validEmail(this.customer.email);
  }

  validEmail(email) {
    const re = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
    return re.test(email);
  }

  matchEmails() {
    this.emailsMatch = this.customer.email.toLowerCase() === this.confirmEmail.toLowerCase();
  }

  togglePromocodesCollapse() {
    this.isPromocodesCollapsed = !this.isPromocodesCollapsed;
    this.promocodeNotValid = false;
    if (this.isPromocodesCollapsed) {
      this.collapseStyles = {'overflow': 'hidden'};
    } else {
      setTimeout(() => {
        this.collapseStyles = {'overflow': 'initial'};
      }, 500);
    }
  }

  toggleBillingCollapse() {
    if (this.requiredDueBillingPrice) {
      return;
    }

    this.isBillingCollapsed = !this.isBillingCollapsed;
    if (this.isBillingCollapsed) {
      this.billingCollapseStyles = {'overflow': 'hidden'};
    } else {
      setTimeout(() => {
        this.billingCollapseStyles = {'overflow': 'initial'};
      }, 500);
    }
  }

  async applyPromocode(): Promise<void> {
    this.promocodeError = undefined;
    this.promocodeNotValid = false;
    if (this._promocode) {
      try {
        const promocode = await this.findPromocode();
        if (this.isPromocodeValid(promocode)) {
          this._globalService.loading = true;
          await this._cartService.applyPromocodeToShoppingCart(this._promocode);
          this.currentPromocode = this._promocode.toUpperCase();
          this.cart = await this._cartService.getCart();
          this._globalService.loading = false;
        } else {
          this.promocodeNotValid = true;
        }
      } catch (e) {
        this.promocodeNotValid = true;
        this.promocodeError = e.error && typeof e.error === 'object' ? e.error.message : e.message;
      }
      if (this.promocodeNotValid) {
        this._promocode = '';
        this.currentPromocode = undefined;
      }
    } else {
      this.promocodeNotValid = true;
      this.promocodeError = 'Promocode is empty.';
    }

    this._globalService.loading = false;
  }

  async removePromocodeFromCart(): Promise<void> {
    this._globalService.loading = true;
    try {
      await this._cartService.applyPromocodeToShoppingCart('');
      this.currentPromocode = undefined;
      this.cart = await this._cartService.getCart();
    } finally {
      this._globalService.loading = false;
    }
  }

  findPromocode(): Promise<Promocode> {
    return new Promise((resolve, reject) => {
      let result: Promocode;
      this.findingPromocode = true;
      this._widgetService.findPromocode(this.cart.bookings[0].event.supplierId, this._promocode).subscribe(
        (promocode: Promocode) => result = promocode,
        (e) => {
          this.findingPromocode = false;
          reject(e.error);
        },
        () => {
          this.findingPromocode = false;
          if (result !== undefined) {
            resolve(result);
          } else {
            reject();
          }
        });
    });
  }

  isPromocodeValid(promocode: Promocode): boolean {
    let result = false;
    if (promocode) {
      const isAfterStart = moment().isAfter(moment(promocode.bookingPeriodStart));
      const isBeforeEnd = moment().isBefore(moment(promocode.bookingPeriodFinish));
      result = isAfterStart && isBeforeEnd && promocode.status === 1 && promocode.promocodeType === 0;
    }

    return result;
  }

  getPromocodeErrorMessage(): string {
    return this.promocodeError || 'Invalid code, please try again';
  }

  async saveCustomer(addToCart = false): Promise<void> {
    this._globalService.customer = this.customer;
    this._globalService.confirmEmail = this.confirmEmail;
    this._globalService.acceptedTerms = this.termsAccepted;
    if (addToCart) {
      await this._cartService.addCustomerToShoppingCart(this.customer, this.billingDetails);
    }
  }

  get isLoading(): boolean {
    return this._globalService.loading;
  }

  get promocode(): string {
    return this._promocode;
  }

  set promocode(value: string) {
    this._promocode = value;
  }

  public isToHideDate(): boolean {
    return this._globalService.hide_date;
  }
}
